/**
 * Italian language strings for the Vivatis PDB application.
 * All user-facing text should be defined here for easy localisation.
 */

const it = {
  /* ------------------------------------------------------------------ */
  /* Global / shared                                                    */
  /* ------------------------------------------------------------------ */
  common: {
    products: 'Prodotti',
    product: 'prodotto',
    productsPlural: 'prodotti',
    partner: 'Partner',
    name: 'Nome',
    category: 'Categoria',
    yes: 'Sì',
    no: 'No',
    back: 'Indietro',
    next: 'Avanti',
    skip: 'Salta',
    filter: 'Filtro',
    filterMode: 'Modalità filtro',
    and: 'E',
    or: 'O',
    clearAll: 'Cancella tutto',
    removeAll: 'Rimuovi tutto',
    browseProducts: 'Sfoglia prodotti',
    noProductsFound: 'Nessun prodotto trovato.',
    noResultsFor: (term: string) => `Nessun risultato per «${term}».`,
    new: 'NUOVO',
    pdf: 'PDF',
    viewAll: 'Visualizza tutto',
    offlineBanner: 'Sei offline. Alcune funzionalità sono limitate.',
    offlineTooltip: 'Non disponibile in modalità offline',
    offlineLanguageHint: 'Offline: la lingua dell’interfaccia cambia, i dati di prodotto restano nella lingua caricata in precedenza finché non torni online',
    pwaUpdateAvailable: 'Una nuova versione è disponibile. Aggiornamento...',
    pwaInstallTitle: 'Installa app',
    pwaInstallDescription: 'Aggiungi alla schermata iniziale per un accesso rapido.',
    pwaInstallButton: 'Installa',
  },

  /* ------------------------------------------------------------------ */
  /* Navigation                                                         */
  /* ------------------------------------------------------------------ */
  nav: {
    home: 'Home',
    products: 'Prodotti',
    wizard: 'Wizard',
    favorites: 'Preferiti',
    compare: 'Confronta',
    cart: 'Documenti',
    searchPlaceholder: 'Cerca prodotti...',
    toggleTheme: 'Cambia tema',
    logout: 'Esci',
    admin: 'Admin',
    toggleMenu: 'Apri/chiudi menu',
  },

  /* ------------------------------------------------------------------ */
  /* Branding                                                           */
  /* ------------------------------------------------------------------ */
  brand: {
    vivatis: 'VIVATIS',
    pdb: 'PDB',
    pharma: 'PHARMA',
    productDatabase: 'Database prodotti',
    copyright: 'Vivatis Pharma GmbH © 2026',
  },

  /* ------------------------------------------------------------------ */
  /* Login                                                              */
  /* ------------------------------------------------------------------ */
  login: {
    email: 'Email',
    password: 'Password',
    showPassword: 'Mostra password',
    hidePassword: 'Nascondi password',
    signingIn: 'Accesso in corso...',
    signIn: 'Accedi',
    forgotPassword: 'Password dimenticata?',
  },

  /* ------------------------------------------------------------------ */
  /* Home page                                                          */
  /* ------------------------------------------------------------------ */
  home: {
    welcomeBack: (name: string) => `Bentornato${name ? `, ${name}` : ''}!`,
    whatToDo: 'Cosa vorresti fare oggi?',
    stats: {
      products: 'Prodotti',
      new: 'Nuovo',
      favorites: 'Preferiti',
      inCart: 'Nel documento',
    },
    actions: {
      browseProducts: 'Sfoglia prodotti',
      viewAllProducts: (count: number) => (count === 1 ? 'Visualizza 1 prodotto' : `Visualizza tutti i ${count} prodotti`),
      browseProductsCount: (count: number) => `${count} prodotti`,
      loading: 'Caricamento...',
      startWizard: 'Avvia Wizard',
      findMatching: 'Trova prodotti corrispondenti',
      favorites: 'Preferiti',
      productsSaved: (count: number) => `${count} salvati`,
      meetingCart: 'Documento riunione',
      productsInCart: (count: number) => `${count} nel documento`,
    },
    browseByIndication: 'Sfoglia per indicazione',
    topIndications: 'Le tue indicazioni principali',
    topIndicationsEmpty: 'Inizia a sfogliare i prodotti per vedere qui le tue indicazioni più consultate.',
    newProducts: 'Nuovi prodotti',
    newProductsTooltip: 'Tutti i prodotti aggiunti negli ultimi 12 mesi sono considerati nuovi.',
    newStudies: 'Nuovi studi',
    newStudiesTooltip: 'Prodotti con studi clinici pubblicati di recente negli ultimi 12 mesi.',
    newStudyBadge: 'Nuovo studio',
    publishedStudies: (count: number) => `${count} studi clinici pubblicati`,
    favoritesTitle: 'Preferiti',
    noFavorites: 'Nessun preferito ancora. Fai clic sull\'icona del cuore su un prodotto per salvarlo qui.',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'prodotto' : 'prodotti'}`,
    recentlyViewed: 'Visualizzati di recente',
    noRecentlyViewed: 'Nessun prodotto visualizzato. Inizia con il',
    productCatalog: 'Catalogo prodotti',
    viewAll: 'Vedi tutti',
  },

  /* ------------------------------------------------------------------ */
  /* Product list                                                       */
  /* ------------------------------------------------------------------ */
  productList: {
    sort: {
      relevance: 'Rilevanza',
      nameAsc: 'Nome (A-Z)',
      nameDesc: 'Nome (Z-A)',
      articleNumber: 'N. articolo',
      partner: 'Partner',
      indication: 'Indicazione',
    },
    viewTable: 'Tabella',
    viewCards: 'Schede',
    productsFound: (count: number) => `${count} prodotti trovati`,
    productCount: (count: number, total: number) => `${count} di ${total} prodotti`,
    loading: 'Caricamento prodotti...',
    adjustFilters: 'Regola i filtri.',
    resetFilters: 'Reimposta filtri',
  },

  /* ------------------------------------------------------------------ */
  /* Product search                                                     */
  /* ------------------------------------------------------------------ */
  productSearch: {
    placeholder: 'Cerca per nome, numero articolo, ingrediente, indicazione...',
  },

  /* ------------------------------------------------------------------ */
  /* Product table                                                      */
  /* ------------------------------------------------------------------ */
  productTable: {
    articleNo: 'N. articolo',
    name: 'Nome',
    partner: 'Partner',
    indication: 'Indicazione',
    application: 'Applicazione',
    country: 'Paese',
    dailyDose: 'Dose giornaliera',
    claims: 'Claim',
    actions: 'Azioni',
  },

  /* ------------------------------------------------------------------ */
  /* Product detail                                                     */
  /* ------------------------------------------------------------------ */
  productDetail: {
    tabs: {
      overview: 'Panoramica',
      productData: 'Dati prodotto',
      moreDetails: 'Prezzo',
    },
    notFound: 'Prodotto non trovato',
    backToOverview: 'Torna alla panoramica',
    marketingClaims: 'Claim di marketing',
    indications: 'Indicazioni',
    applications: 'Applicazioni',
    countries: 'Paesi',
    description: 'Descrizione',
    shortDescription: 'Descrizione breve',
    website: 'Sito web del prodotto',
    multimedia: 'Sito web multimediale',
    studies: 'Studi',
    origin: 'Origine',
    dosage: 'Dosaggio',
    appearance: 'Aspetto',
    category: 'Categoria',
    articleNumber: 'Numero articolo',
    active: 'Attivo',
    created: 'Creato',
    showDailyPrice: 'Mostra prezzo giornaliero',
    perDay: '/ giorno',
    generatingPdf: 'Generazione PDF in corso...',
    sendViaEmail: 'Invia via email',
    emailSubject: (name: string, companyName: string) => `${companyName} — Scheda prodotto: ${name}`,
    emailBody: (name: string, userName: string, companyName: string) =>
      `Gentile cliente,\n\nIn allegato trova la scheda prodotto per ${name}.\n\nNon esiti a contattarmi per qualsiasi domanda o per ulteriori informazioni.\n\nCordiali saluti,\n${userName}\n${companyName}`,
    toastEmail: 'Email inviata con successo!',
  },

  /* ------------------------------------------------------------------ */
  /* Actions (favorite, cart, compare)                                   */
  /* ------------------------------------------------------------------ */
  actions: {
    addToFavorites: 'Aggiungi ai preferiti',
    removeFromFavorites: 'Rimuovi dai preferiti',
    favorited: 'Preferito',
    favorite: 'Preferito',
    addToCart: 'Aggiungi al documento',
    removeFromCart: 'Rimuovi dal documento',
    removeFromAllCarts: 'Rimuovi da tutti i documenti',
    inCart: 'Nel documento',
    addToCartLabel: 'Aggiungi al documento',
    inCartLabel: 'Nel documento',
    cartLabel: 'Documenti',
    addToComparison: 'Aggiungi al confronto',
    removeFromComparison: 'Rimuovi dal confronto',
    inComparison: 'Nel confronto',
    compared: 'Confrontato',
    compare: 'Confronta',
    manageFavorites: 'Gestisci preferiti',
    addToFavoritesNoGroup: 'Aggiungi ai preferiti (senza gruppo)',
    favoritesLabel: 'Preferiti',
  },

  /* ------------------------------------------------------------------ */
  /* Favorites page                                                     */
  /* ------------------------------------------------------------------ */
  favorites: {
    title: 'Preferiti',
    noFavorites: 'Nessun preferito',
    noFavoritesDescription: 'Contrassegna i prodotti come preferiti per vederli qui.',
    deleteGroup: {
      title: 'Elimina gruppo',
      message: (name: string) => `Sei sicuro di voler eliminare "${name}"?`,
      moveLabel: 'Sposta prodotti in:',
      ungroupedOption: 'Non in un gruppo (non raggruppato)',
      confirm: 'Elimina gruppo',
      cancel: 'Annulla',
    },
    browseProducts: 'Sfoglia prodotti',
    dragProductsHint: 'Trascina i prodotti qui',
    notInGroup: 'Non in un gruppo',
    notInGroupWithCount: (count: number) => `Non in un gruppo (${count})`,
    dragCopyHint: '⌘/⇧ tenuto — rilascia per copiare nel gruppo',
    dragMoveHint: 'Rilascia per spostare · tieni ⌘/⇧ per copiare',
    addGroupToDocument: 'Aggiungi gruppo al documento',
    deleteGroupTitle: 'Elimina gruppo',
    groupNamePlaceholder: 'Nome gruppo...',
    create: 'Crea',
    cancel: 'Annulla',
    newGroup: 'Nuovo gruppo',
    inDocument: 'Nel documento',
    alreadyInDocument: 'I prodotti sono già nel documento.',
    addedToDocument: 'Prodotti aggiunti al documento.',
    removedFromDocument: 'Prodotti rimossi dal documento.',
    addToDocument: 'Aggiungi',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'prodotto' : 'prodotti'}`,
    openProductTitle: 'Apri',
  },

  /* ------------------------------------------------------------------ */
  /* Comparison page                                                    */
  /* ------------------------------------------------------------------ */
  comparison: {
    title: 'Confronto prodotti',
    loading: 'Caricamento prodotti...',
    productCount: (count: number, max: number) => `(${count}/${max})`,
    noProducts: 'Nessun prodotto da confrontare',
    noProductsDescription: 'Aggiungi prodotti per confrontarli affiancati.',
    browseProducts: 'Sfoglia prodotti',
    addProduct: 'Aggiungi prodotto',
    removeFromComparison: 'Rimuovi dal confronto',
    removeAll: 'Rimuovi tutto',
    showDifferencesOnly: 'Mostra solo le differenze',
    showingDifferencesOnly: 'Visualizzazione solo delle differenze',
    hideDailyPrice: 'Nascondi prezzo giornaliero',
    dailyPriceHidden: 'Prezzo giornaliero nascosto',
    hidePrice: 'Nascondi prezzo',
    priceHidden: 'Prezzo nascosto',
    allIdentical: 'Tutti i valori sono identici per questi prodotti.',
    rows: {
      articleName: 'Nome articolo',
      articleNumber: 'Numero articolo',
      partner: 'Partner',
      manufacturer: 'Produttore',
      category: 'Categoria',
      clinicalStudies: 'Studi clinici',
      bioactiveIngredients: 'Ingredienti bioattivi / Dosaggio',
      indications: 'Indicazioni',
      applications: 'Applicazioni',
      countries: 'Paesi',
      dosage: 'Dosaggio',
      dailyDosage: 'Dose giornaliera',
      appearance: 'Aspetto',
      origin: 'Origine',
      sourceOrigin: 'Fonte / Origine',
      marketingClaims: 'Claim di marketing',
      dailyPrice: 'Prezzo giornaliero',
      price: 'Prezzo',
      novelFood: 'Novel Food',
    },
    novelFoodStatuses: {
      yes: 'Sì',
      ongoing: 'In corso',
      approved: 'Approvato',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Cart page                                                          */
  /* ------------------------------------------------------------------ */
  cart: {
    title: 'Documento riunione',
    subtitle: 'Prodotti per il tuo incontro con il cliente',
    emptyTitle: 'Il tuo documento è vuoto',
    emptyDescription: 'Aggiungi prodotti dal catalogo.',
    moveUp: 'Sposta su',
    moveDown: 'Sposta giù',
    remove: 'Rimuovi',
    rename: 'Rinomina',
    lockedNotice: (date: string) => `Questo documento è stato inviato il ${date} e non può più essere modificato.`,
    generatePdf: 'Genera PDF',
    generatingPdf: 'Generazione PDF in corso...',
    sendViaEmail: 'Invia via email',
    createOpportunity: 'Crea opportunità in SalesForce',
    clearCart: 'Svuota documento',
    archiveDocument: 'Archivia documento',
    confirmArchive: (name: string) => `Archiviare "${name}"? Non apparirà più nell'elenco dei documenti e successivamente sarà visibile solo agli amministratori.`,
    deleteDocument: 'Elimina documento',
    confirmClear: 'Sei sicuro di voler svuotare il documento?',
    confirmDelete: (name: string) => `Eliminare "${name}"? Il documento e tutti i suoi prodotti verranno rimossi.`,
    toastPdf: 'Il PDF è stato generato e scaricato.',
    toastPdfError: 'Generazione PDF non riuscita.',
    toastEmail: 'Email inviata con successo!',
    showDocuments: 'Mostra documenti',
    newDocument: 'Nuovo documento',
    searchDocuments: 'Cerca documenti...',
    noDocumentsFound: 'Nessun documento trovato.',
    items: 'elementi',
    comingSoon: 'Prossimamente',
    comingSoonDescription: 'L\'integrazione con SalesForce è attualmente in fase di sviluppo. Questa funzionalità sarà disponibile in un futuro aggiornamento.',
    productCount: (count: number) => `${count} ${count === 1 ? 'prodotto' : 'prodotti'}`,
    emailSubject: (count: number, companyName: string) => `${companyName} — Selezione prodotti (${count} prodotti)`,
    emailBody: (productList: string, userName: string, companyName: string) => `Gentile cliente,\n\nDi seguito trova la selezione di prodotti dal nostro ultimo incontro:\n\n${productList}\n\nNon esiti a contattarmi per qualsiasi domanda o per ulteriori informazioni su uno di questi prodotti.\n\nCordiali saluti,\n${userName}\n${companyName}`,
    email: {
      title: 'Invia via email',
      recipients: 'Destinatari',
      subject: 'Oggetto',
      message: 'Messaggio',
      placeholder: 'Inserisci indirizzo email...',
      cancel: 'Annulla',
      send: 'Invia email',
      sending: 'Invio in corso...',
      invalidEmail: 'Inserire un indirizzo email valido.',
      duplicateEmail: 'Questo indirizzo email è già stato aggiunto.',
      sendError: 'Invio dell\'email non riuscito. Riprova.',
      products: 'Prodotti',
      productsCount: (selected: number, total: number) => `${selected} di ${total}`,
      selectAll: 'Seleziona tutto',
      deselectAll: 'Deseleziona tutto',
      noProductsWarning: 'Seleziona almeno un prodotto da includere nell\'email.',
      fileTypesLegend: 'Allega documenti',
      fileTypesLoading: 'Caricamento dei documenti disponibili…',
      fileTypes: {
        study: 'Studi',
        marketing: 'Documenti di marketing',
        quality: 'Documenti di qualità',
      },
      qualityHint: 'I documenti di qualità non vengono allegati. {email} li invia separatamente ai destinatari.',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Settings                                                           */
  /* ------------------------------------------------------------------ */
  settings: {
    title: 'Impostazioni',
    subtitle: 'Configura le preferenze dell\'applicazione',
    multiCart: {
      title: 'Documento multiplo',
      description:
        'Abilita più documenti riunione per organizzare i prodotti per clienti o riunioni diverse. Puoi avere fino a 3 documenti e passare da uno all\'altro dall\'intestazione.',
    },
    favoritesOnHome: {
      title: 'Preferiti sulla dashboard',
      description: 'Mostra la sezione preferiti nella schermata iniziale per un accesso rapido ai prodotti salvati.',
    },
    favoriteGroups: {
      title: 'Gruppi di preferiti',
      description: 'Organizza i tuoi preferiti in gruppi. Puoi creare fino a 5 gruppi e trascinare i prodotti tra di essi. Ogni gruppo può essere aggiunto a un documento con un clic.',
    },
    productData: {
      title: 'Dati dei prodotti',
      description: 'Mostra quando i dati dei prodotti sono stati caricati l\'ultima volta dal server.',
      lastLoaded: 'Ultimo caricamento',
      never: 'Mai caricato',
      reload: 'Ricarica prodotti',
      reloading: 'Ricaricamento...',
      reloadSuccess: 'Dati dei prodotti ricaricati',
      reloadError: 'Impossibile ricaricare i dati dei prodotti',
    },
    changePassword: {
      title: 'Cambia password',
      description: 'Aggiorna la password che utilizzi per accedere.',
      currentPassword: 'Password attuale',
      newPassword: 'Nuova password',
      confirmPassword: 'Conferma nuova password',
      submit: 'Aggiorna password',
      submitting: 'Aggiornamento...',
      success: 'La tua password è stata aggiornata.',
      passwordMismatch: 'Le password non corrispondono',
      error: 'Impossibile aggiornare la password',
      backToSettings: 'Torna alle impostazioni',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Wizard                                                             */
  /* ------------------------------------------------------------------ */
  wizard: {
    steps: {
      country: 'Paese',
      application: 'Applicazione',
      indication: 'Indicazione',
    },
    selectCountry: 'Seleziona paese',
    selectCountrySubtitle: 'In quali paesi dovrebbe essere disponibile il prodotto?',
    selectApplication: 'Seleziona applicazione',
    selectApplicationSubtitle: 'Quale forma di dosaggio preferisci?',
    selectIndication: 'Seleziona indicazione',
    selectIndicationSubtitle: 'Quali aree della salute sono rilevanti?',
    showResults: 'Mostra risultati',
    noSelection: 'Nessuna selezione effettuata',
    selected: (count: number) => `${count} selezionati`,
    filterAnd: 'E',
    filterOr: 'O',
    filterAndExplanation: 'I prodotti devono corrispondere a tutti i criteri selezionati',
    filterOrExplanation: 'I prodotti devono corrispondere ad almeno un criterio per passaggio',
  },

  /* ------------------------------------------------------------------ */
  /* Filter sidebar                                                     */
  /* ------------------------------------------------------------------ */
  filterSidebar: {
    title: 'Filtro',
    category: 'Categoria',
    country: 'Paese',
    application: 'Applicazione',
    indication: 'Indicazione',
    partner: 'Partner',
    marketingClaims: 'Claim di marketing',
    agreement: 'Accordo',
    agreements: {
      DA: 'DA — Contratto di distribuzione',
      CPA: 'CPA — Protezione cliente',
      Gentleman: 'Gentleman Agreement',
      LOI: 'LOI — Lettera d’intenti',
      none: 'Nessun accordo',
    },
    agreementDescriptions: {
      DA: 'Contratto di distribuzione (esclusivo)',
      CPA: 'Accordo di protezione cliente (non esclusivo)',
      Gentleman: 'Gentleman Agreement (verbale, senza contratto)',
      LOI: 'Lettera d’intenti (fase preliminare del DA)',
      none: 'Nessun accordo firmato per questo paese',
    },
    newProducts: 'Nuovi prodotti',
    newStudy: 'Nuovo studio',
    restrictedTo: (territories: string) => `La tua vista è limitata a ${territories}.`,
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------ */
  /* Not Found (404)                                                    */
  /* ------------------------------------------------------------------ */
  notFound: {
    title: '404',
    heading: 'Pagina non trovata',
    description: 'La pagina che stai cercando non esiste o è stata spostata.',
    goBack: 'Torna indietro',
    home: 'Home',
  },

  /* ------------------------------------------------------------------ */
  /* Forgot Password                                                    */
  /* ------------------------------------------------------------------ */
  forgotPassword: {
    subtitle: 'Reimposta la tua password',
    email: 'Email',
    emailPlaceholder: 'tua@email.com',
    submit: 'Invia link di reimpostazione',
    submitting: 'Invio in corso...',
    success: 'Abbiamo inviato un link per la reimpostazione della password al tuo indirizzo email.',
    backToLogin: 'Torna al login',
    error: 'Impossibile inviare il link di reimpostazione',
  },

  /* ------------------------------------------------------------------ */
  /* Reset Password                                                     */
  /* ------------------------------------------------------------------ */
  resetPassword: {
    subtitle: 'Imposta una nuova password',
    email: 'Email',
    newPassword: 'Nuova password',
    confirmPassword: 'Conferma password',
    submit: 'Reimposta password',
    submitting: 'Reimpostazione in corso...',
    passwordMismatch: 'Le password non corrispondono',
    backToLogin: 'Torna al login',
    error: 'Impossibile reimpostare la password',
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  claimDescriptions: {
    Vegan: 'Non contiene ingredienti o sottoprodotti di origine animale.',
    Organic: 'Certificato biologico — coltivato senza pesticidi o fertilizzanti sintetici.',
    'Non-GMO': 'Privo di organismi geneticamente modificati.',
    'Clinically Studied': 'Efficacia supportata da studi clinici pubblicati.',
    Patented: 'Protetto da uno o più brevetti per formulazione o processo unico.',
    Halal: 'Certificato Halal — conforme alle linee guida alimentari islamiche.',
    Kosher: 'Certificato Kosher — conforme alle leggi alimentari ebraiche.',
    'Gluten-Free': 'Non contiene glutine — sicuro per persone con celiachia o sensibilità al glutine.',
  },
  pdf: {
    productDataSheet: 'Scheda prodotto',
    productDataSheets: 'Schede prodotto',
    keyFacts: 'Dati chiave',
    dailyDosage: 'Dose giornaliera',
    appearance: 'Aspetto',
    bioactiveIngredient: 'Ingrediente bioattivo',
    indications: 'Indicazioni',
    possibleApplications: 'Applicazioni possibili',
    description: 'Descrizione',
    linkStudies: 'Studi',
    origin: 'Origine',
    confidential: 'Riservato -- Solo per uso commerciale',
    productCountOne: ':count prodotto',
    productCountOther: ':count prodotti',
    clinicalStudies: 'Studi clinici',
    marketingClaims: 'Claim di marketing',
    territories: 'Territori',
    moq: "Quantita minima d'ordine in kg",
    shelfLife: 'Durata di conservazione',
    shelfLifeValue: ':months mesi',
    novelFoodStatus: 'Novel Food',
    badgeNew: 'NUOVO',
    badgeNewStudy: 'NUOVO STUDIO',
    contactPerson: 'Il tuo contatto presso Vivatis:',
    website: 'Sito web del prodotto',
    multimedia: 'Sito web multimediale',
  },
} as const;

export default it;
